import { createSelector } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { isDataScopePermission, moduleRequiresAccessPermission } from 'src/app/core/store/permissions/permissions.catalog';
import { IModule, IRole } from '../models/settings.models';

export interface RolePermissionActionView {
  action: string;
  permissionId: number;
  permissionName: string;
}

export interface RolePermissionModuleView {
  id: number;
  name: string;
  label: string;
  hasAccessToggle: boolean;
  accessPermissionId: number | null;
  dataScopePermissionId: number | null;
  actionPermissions: RolePermissionActionView[];
  permissionIds: number[];
}

export const selectSettingsState = (state: AppState) => state.settings;

export const selectAllRoles = createSelector(
  selectSettingsState,
  (state) => state.allRoles
);

export const selectAllModules = createSelector(
  selectSettingsState,
  (state) => state.allModules
);

export const selectRolePermissionCatalog = createSelector(
  selectAllModules,
  (modules: IModule[]) => buildRolePermissionCatalog(modules)
);

export function buildRolePermissionCatalog(
  modules: IModule[]
): RolePermissionModuleView[] {
  return (modules ?? []).map((module) => {
    const hasAccessToggle = moduleRequiresAccessPermission(module.name);
    const allPermissions = [...(module.permissions ?? [])];
    const accessPermission = allPermissions.find(
      (permission) => getPermissionAction(permission.name) === 'access'
    );
    const dataScopePermission = allPermissions.find(
      (permission) => isDataScopePermission(permission.name)
    );
    const sortedPermissions = allPermissions
      .filter((permission) =>
        (hasAccessToggle || getPermissionAction(permission.name) !== 'access') &&
        !isDataScopePermission(permission.name)
      )
      .sort((left, right) => {
        return getPermissionActionOrder(left.name) - getPermissionActionOrder(right.name);
      });
    const actionPermissions = sortedPermissions
      .filter((permission) => getPermissionAction(permission.name) !== 'access')
      .map((permission) => ({
        action: getPermissionAction(permission.name),
        permissionId: permission.id,
        permissionName: permission.name,
      }));

    return {
      id: module.id,
      name: module.name,
      label: humanizeModuleName(module.name),
      hasAccessToggle,
      accessPermissionId: accessPermission?.id ?? null,
      dataScopePermissionId: dataScopePermission?.id ?? null,
      actionPermissions,
      permissionIds: sortedPermissions.map((permission) => permission.id),
    };
  });
}

export function mergeRolePermissionIds(role: IRole | null | undefined): Set<number> {
  const selectedIds = new Set<number>();

  for (const permission of role?.permissions ?? []) {
    selectedIds.add(permission.id);
  }

  for (const permissionId of role?.permission_ids ?? []) {
    selectedIds.add(permissionId);
  }

  return selectedIds;
}

export function countSelectedRoleModules(
  permissionCatalog: RolePermissionModuleView[],
  role: IRole | null | undefined
): number {
  const selectedPermissionIds = mergeRolePermissionIds(role);

  if (role?.is_protected) {
    return permissionCatalog.length;
  }

  return permissionCatalog.filter((module) =>
    module.permissionIds.some((permissionId) => selectedPermissionIds.has(permissionId))
  ).length;
}

function humanizeModuleName(moduleName: string): string {
  return (moduleName ?? '')
    .split('_')
    .filter(Boolean)
    .map((token) => `${token.charAt(0).toUpperCase()}${token.slice(1)}`)
    .join(' ');
}

function getPermissionAction(permissionName: string): string {
  return (permissionName ?? '').split(':')[0]?.toLowerCase() || 'access';
}

function getPermissionActionOrder(permissionName: string): number {
  const actionOrder: Record<string, number> = {
    access: 0,
    create: 1,
    edit: 2,
    approve: 3,
    delete: 4,
  };

  return actionOrder[getPermissionAction(permissionName)] ?? 99;
}
